import { captureException } from '@sentry/browser';
import { mergeAll } from 'lodash/fp';
import { FetchParams, Middleware, RequestContext, ResponseContext } from 'api';
import applicationConfig from 'core/config/local';
import { ApiError, ErrorInterface } from 'core/api/errors';
import type { RootAppState } from 'store/types';

export function accessMiddleware(access: string): Middleware {
  return {
    pre: async (context: RequestContext): Promise<FetchParams | void> => {
      return access ? mergeAll([context, { init: { headers: { Authorization: 'Bearer ' + access } } }]) : context;
    },
  };
}

export function authMiddleware<S extends RootAppState = RootAppState>(getState: () => S): Middleware {
  return {
    pre: async (context: RequestContext): Promise<FetchParams | void> => {
      // Session-based auth: authentication is handled by httpOnly cookies
      // No Authorization header needed - the session cookie is automatically sent with credentials: 'include'
      return context;
    },
  };
}

export function csrfMiddleware(): Middleware {
  return {
    pre: async (context: RequestContext): Promise<FetchParams | void> => {
      // Add CSRF token from cookie to header for state-changing requests
      const method = context.init.method?.toUpperCase() || 'GET';
      const needsCSRF = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

      if (needsCSRF) {
        const csrfToken = getCsrfTokenFromCookie();
        if (csrfToken) {
          return mergeAll([context, { init: { headers: { 'X-CSRFToken': csrfToken } } }]);
        }
      }

      return context;
    },
  };
}

function getCsrfTokenFromCookie(): string | null {
  const isHttps = window.location.protocol === 'https:';
  const cookieName = isHttps ? '__Host-cashlink-csrftoken' : 'csrftoken';

  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === cookieName) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

export function errorMiddleware(): Middleware {
  return {
    post: async (context: ResponseContext): Promise<Response | void> => {
      const { response } = context;

      if (response.status >= 200 && response.status < 300) return response;

      let info: ErrorInterface | undefined;
      try {
        const data: any = await response.json();
        if (data.hasOwnProperty('error_codes')) info = data as ErrorInterface;
      } catch (e) {
        // Might not be a JSON response, such as 500 or 503
      }

      if (!info && applicationConfig.sentry) {
        captureException(new ApiError(info, response));
      }

      throw new ApiError(info, response);
    },
  };
}
